import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { App as NativeApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '../services/api';
import { isNative, NATIVE_AUTH_REDIRECT } from '../services/platform';
import { OWNED_KEYS } from '../utils/ownerStorage';
import { forgetOwnerHistory } from '../utils/progressHistory';

const AuthContext = createContext(null);

export const PLAN_STORAGE_KEY = 'financialPlanState_v2';

const NOT_CONFIGURED = 'Sign-in is unavailable right now. Please try again later.';

function mapUser(supabaseUser) {
  if (!supabaseUser) return null;
  const meta = supabaseUser.user_metadata || {};
  let firstName = meta.first_name || '';
  let lastName = meta.last_name || '';
  if (!firstName && meta.full_name) {
    const parts = meta.full_name.split(' ');
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ') || '';
  }
  if (!firstName && meta.name) {
    const parts = meta.name.split(' ');
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ') || '';
  }
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    firstName,
    lastName,
    avatarUrl: meta.avatar_url || meta.picture || null,
  };
}

// Where auth emails and OAuth send the user back to. Excludes the hash so the
// PKCE `?code=` parameter does not collide with HashRouter.
function authRedirectUrl() {
  return isNative ? NATIVE_AUTH_REDIRECT : window.location.origin + window.location.pathname;
}

// Plans on the device are managed by FinancialPlanContext. Deleting an account
// also removes that account's working copy from the device.
//
// What an account leaves in this browser when it signs out: nothing that is
// also in the cloud or can be worked out again (the session, the synced plan,
// the progress history). Work that exists only here (unsynced edits, plans
// replaced by a cloud copy, real estate entries) stays, filed under the
// account, where no other account and no signed-out visitor is shown it.
// Deleting the account removes all of it.
function clearLocalSession({ signedOutUserId = null, deletedUserId = null } = {}) {
  try {
    sessionStorage.clear();
    const deleted = deletedUserId
      ? [`${PLAN_STORAGE_KEY}:${deletedUserId}`, `${PLAN_STORAGE_KEY}:backup:${deletedUserId}`, ...OWNED_KEYS.map((key) => `${key}:${deletedUserId}`)]
      : [];
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') || deleted.includes(key)) localStorage.removeItem(key);
    });
  } catch {
    // Storage unavailable: nothing to clear.
  }
  const gone = deletedUserId || signedOutUserId;
  if (gone) forgetOwnerHistory(gone);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const sessionRef = useRef(null);
  // Accounts deleted in this session, so nothing of theirs is written back to the device.
  const deletedUsers = useRef(new Set());
  const wasAccountDeleted = useCallback((id) => deletedUsers.current.has(id), []);
  // True between starting a native sign-in, sign-up or reset and its deep link back.
  const authFlowPending = useRef(false);

  const isAuthenticated = !!session;

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Safety net: never let a hung/failed getSession() leave the app stuck on
    // the loading state, which would hide the entire auth UI (Sign In + avatar).
    let settled = false;
    const finishLoading = () => {
      if (settled) return;
      settled = true;
      setLoading(false);
    };
    const timeoutId = setTimeout(finishLoading, 4000);

    supabase.auth.getSession()
      .then(({ data: { session: s } }) => {
        setSession(s);
        sessionRef.current = s;
        setUser(mapUser(s?.user ?? null));
      })
      .catch((err) => {
        console.warn('Supabase getSession failed:', err?.message || err);
      })
      .finally(finishLoading);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      setSession(s);
      sessionRef.current = s;
      setUser(mapUser(s?.user ?? null));
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  // Native apps finish OAuth, email confirmation, and password reset through a
  // deep link back into the app carrying the PKCE code.
  useEffect(() => {
    if (!isNative || !supabase) return undefined;
    const listener = NativeApp.addListener('appUrlOpen', async ({ url }) => {
      if (!url || !url.startsWith(NATIVE_AUTH_REDIRECT)) return;
      // Any app or web page can open this link. Only act on it when this app
      // started a sign-in, and never display text taken from the link.
      if (!authFlowPending.current) return;
      authFlowPending.current = false;
      Browser.close().catch(() => {});
      const code = new URL(url).searchParams.get('code');
      if (!code) {
        setError('Sign-in was not completed. Please try again.');
        return;
      }
      const { error: err } = await supabase.auth.exchangeCodeForSession(code);
      if (err) setError('Sign-in could not be completed. Please try again.');
    });
    return () => { listener.then((handle) => handle.remove()); };
  }, []);

  const fail = useCallback((message) => {
    setError(message);
    throw new Error(message);
  }, []);

  const login = useCallback(async (email, password) => {
    if (!supabase) fail(NOT_CONFIGURED);
    setError(null);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) {
      setError(err.message);
      throw err;
    }
    return mapUser(data.user);
  }, [fail]);

  const loginWithProvider = useCallback(async (provider) => {
    if (!supabase) fail(NOT_CONFIGURED);
    setError(null);
    // Google blocks OAuth inside embedded WebViews, so native builds open the
    // system browser instead of navigating the app itself.
    const { data, error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: authRedirectUrl(), skipBrowserRedirect: isNative },
    });
    if (err) {
      setError(err.message);
      throw err;
    }
    if (isNative && data?.url) {
      authFlowPending.current = true;
      await Browser.open({ url: data.url });
    }
  }, [fail]);

  // Resolves with needsEmailConfirmation when the project requires the user to
  // confirm their address before a session is issued.
  const register = useCallback(async ({ firstName, lastName, email, password }) => {
    if (!supabase) fail(NOT_CONFIGURED);
    setError(null);
    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { first_name: firstName, last_name: lastName },
        emailRedirectTo: authRedirectUrl(),
      },
    });
    if (err) {
      setError(err.message);
      throw err;
    }
    if (!data.session) authFlowPending.current = true;
    return { user: mapUser(data.user), needsEmailConfirmation: !data.session };
  }, [fail]);

  const requestPasswordReset = useCallback(async (email) => {
    if (!supabase) fail(NOT_CONFIGURED);
    setError(null);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: authRedirectUrl() });
    if (err) {
      setError(err.message);
      throw err;
    }
    authFlowPending.current = true;
  }, [fail]);

  const updatePassword = useCallback(async (password) => {
    if (!supabase) fail(NOT_CONFIGURED);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) throw err;
    setPasswordRecovery(false);
  }, [fail]);

  const logout = useCallback(async () => {
    if (!supabase) return;
    try {
      await supabase.auth.signOut();
    } catch {
      // Ensure cleanup runs even if signOut fails
    }
    clearLocalSession({ signedOutUserId: sessionRef.current?.user?.id || null });
    setUser(null);
    setSession(null);
    sessionRef.current = null;
  }, []);

  // Permanently deletes the auth user and their synced data. The deletion runs
  // in the `delete-account` Edge Function because it needs the service role.
  const deleteAccount = useCallback(async () => {
    if (!supabase || !sessionRef.current) throw new Error('You must be signed in to delete your account.');
    const { error: err } = await supabase.functions.invoke('delete-account', { method: 'POST' });
    if (err) throw new Error('Your account could not be deleted. Please try again or contact support.');
    const deletedUserId = sessionRef.current.user.id;
    deletedUsers.current.add(deletedUserId);
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // The user no longer exists server-side; local cleanup below is what matters.
    }
    clearLocalSession({ deletedUserId });
    setUser(null);
    setSession(null);
    sessionRef.current = null;
  }, []);

  const loadFinancialData = useCallback(async () => {
    const s = sessionRef.current;
    if (!supabase || !s) return null;
    const { data, error: err } = await supabase
      .from('financial_data')
      .select('data, updated_at')
      .eq('user_id', s.user.id)
      .maybeSingle();
    if (err) throw err;
    // `stamp` is the server's exact version marker; saves are conditional on it.
    return data?.data ? { data: data.data, stamp: data.updated_at, updatedAt: Date.parse(data.updated_at) || 0 } : null;
  }, []);

  /**
   * Saves the plan only if the cloud still holds the version this device last
   * saw (`base`), so one device can never silently overwrite another's
   * changes. Rejects with `conflict: true` when it does not.
   */
  const saveFinancialData = useCallback(async (financialData, { userId, base } = {}) => {
    const s = sessionRef.current;
    if (!supabase || !s) throw new Error('Not signed in.');
    // The plan in memory must belong to the account that is signed in now.
    if (userId && s.user.id !== userId) throw new Error('The signed-in account changed.');
    const conflict = () => Object.assign(new Error('The plan was changed on another device.'), { conflict: true });

    if (base) {
      const { data, error: err } = await supabase
        .from('financial_data')
        .update({ data: financialData })
        .eq('user_id', s.user.id)
        .eq('updated_at', base)
        .select('updated_at');
      if (err) throw err;
      if (!data || data.length !== 1) throw conflict();
      return { stamp: data[0].updated_at };
    }

    const { data, error: err } = await supabase
      .from('financial_data')
      .insert({ user_id: s.user.id, data: financialData })
      .select('updated_at');
    if (err) {
      // A row already exists that this device has not seen.
      if (err.code === '23505') throw conflict();
      throw err;
    }
    return { stamp: data[0].updated_at };
  }, []);

  const updateProfile = useCallback(async ({ firstName, lastName }) => {
    if (!supabase) return;
    const { data, error: err } = await supabase.auth.updateUser({
      data: { first_name: firstName, last_name: lastName },
    });
    if (err) throw err;
    setUser(mapUser(data.user));
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    passwordRecovery,
    login,
    loginWithProvider,
    register,
    requestPasswordReset,
    updatePassword,
    cancelPasswordRecovery: () => setPasswordRecovery(false),
    logout,
    deleteAccount,
    loadFinancialData,
    saveFinancialData,
    wasAccountDeleted,
    updateProfile,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
